import type AccountProgramInfo from "~/domains/payment/account/AccountProgramInfo";
import type { ProgramInfoStatusType } from "~/domains/program/ProgramInfoStatusType";
import type AccountPaymentInfo from "~/domains/payment/account/AccountPaymentInfo";
import type DiscountInfo from "~/domains/payment/discounts/DiscountInfo";
import type TaxInfo from "~/domains/payment/taxes/TaxInfo";
import type AccountDiscountStrategy from "~/domains/payment/discounts/account/account/AccountDiscountStrategy";
import type ProgramDiscountStrategy from "~/domains/payment/discounts/account/program/ProgramDiscountStrategy";
import CustomerProgramDiscountStrategy from "~/domains/payment/discounts/account/program/CustomerProgramDiscountStrategy";
import _ from "lodash";
import { roundPrice } from "~/utils/payment";
import type AccountProgramServiceInfo from "~/domains/payment/account/AccountProgramServiceInfo";
import type ProgramServiceTaxStrategy from "~/domains/payment/taxes/program/service/ProgramServiceTaxStrategy";
import SalesProgramServiceTaxStrategy from "~/domains/payment/taxes/program/service/SalesProgramServiceTaxStrategy";
import type ProgramServiceDiscountStrategy from "~/domains/payment/discounts/account/program/service/ProgramServiceDiscountStrategy";
import PrePayProgramServiceDiscountStrategy from "~/domains/payment/discounts/account/program/service/PrePayProgramServiceDiscountStrategy";
import ProgramProgramServiceDiscountStrategy from "~/domains/payment/discounts/account/program/service/ProgramProgramServiceDiscountStrategy";

export default class AccountService {
  activeProgramStates: ProgramInfoStatusType[] = ["8", "9"];
  activeServiceStatuses: string[] = ["Y", "Q", "S", "M", "*", "$"];
  serviceDiscountStrategies: ProgramServiceDiscountStrategy[];
  programDiscountStrategies: ProgramDiscountStrategy[];
  accountDiscountStrategies: AccountDiscountStrategy[];
  programServiceTaxStrategies: ProgramServiceTaxStrategy[];

  constructor() {
    this.serviceDiscountStrategies = [
      new ProgramProgramServiceDiscountStrategy(),
      new PrePayProgramServiceDiscountStrategy(),
    ];
    this.programDiscountStrategies = [
      new CustomerProgramDiscountStrategy(), // TODO: Refactor into service-level discount?
    ];
    this.accountDiscountStrategies = [
      // new PrePayAccountDiscountStrategy(),
    ];
    this.programServiceTaxStrategies = [
      new SalesProgramServiceTaxStrategy(),
    ];
  }

  public getAccountPaymentInfo(accountInfo: AccountPaymentInfo): AccountPaymentInfo {
    const accountPaymentInfo: AccountPaymentInfo = {
      programs: accountInfo.programs,
      services: accountInfo.services,
      programCodes: accountInfo.programCodes,
      franchiseDiscounts: accountInfo.franchiseDiscounts,
      franchiseServices: accountInfo.franchiseServices,
      discountCodes: accountInfo.discountCodes,
      taxesInfo: accountInfo.taxesInfo,
      payType: accountInfo.payType,
      installmentBillingMonthsRemaining: accountInfo.installmentBillingMonthsRemaining,
      accountBalance: (accountInfo.accountBalance ?? 0),
      totalInitialPayment: 0,
      subtotal: 0,
      // monthlySubtotal: 0,
      discounts: [],
      // monthlyDiscounts: [],
      totalDiscount: 0,
      // totalMonthlyDiscount: 0,
      totalPrice: 0,
      // totalMonthlyPrice: 0,
      taxes: [],
      // monthlyTaxes: [],
      totalTax: 0,
      // totalMonthlyTax: 0,
      totalDue: 0,
      // totalMonthlyDue: 0,
      dueToday: 0,
      // dueMonthly: 0,
    };

    // Set isPaid for services that have been pre-paid
    accountPaymentInfo.services = accountPaymentInfo.services.map((service) => ({
      ...service,
      isPaid: service.isPaid || (!!service.prepayId && !!service.prepayAmount),
    }));

    /* Calculate programs */
    accountPaymentInfo.programs = accountPaymentInfo.programs
      .filter((program) => this.activeProgramStates.includes(program.status) || program.doneToDate > 0)
      .map((accountProgram) => {
        const program: AccountProgramInfo = {
          ...accountProgram,
          totalInitialPayment: 0,
          subtotal: 0,
          // monthlySubtotal: 0,
          discounts: [],
          // monthlyDiscounts: [],
          totalDiscount: 0,
          // totalMonthlyDiscount: 0,
          totalPrice: 0,
          // totalMonthlyPrice: 0,
          taxes: [],
          // monthlyTaxes: [],
          totalTax: 0,
          // totalMonthlyTax: 0,
          totalDue: 0,
          // totalMonthlyDue: 0,
        };

        accountPaymentInfo.services = accountPaymentInfo.services.filter((service) => this.activeServiceStatuses.includes(service.serviceStatus));
        const programServices = this.getUnpaidProgramServices(program.id, accountPaymentInfo.services);
        program.services = programServices;

        programServices.forEach((service) => {
          // Service Subtotal
          switch (accountPaymentInfo.payType) {
            case "autoPay":
            case "payAfterEach":
              service.subtotal = service.doneDate ? (service.price ?? 0) : 0; // Include only completed unpaid services.
              break;
            case "autoPayInstallment":
            case "payNow":
              service.subtotal = (service.price ?? 0); // Include all unpaid services, TODO: Not already in an installment plan?
              break;
          }

          // Service Discounts
          accountPaymentInfo.discountCodes
            .filter((discountCode) => discountCode.id === service.discountCode)
            .forEach((serviceDiscountCode) => {
              const serviceDiscountValue = serviceDiscountCode.discountAmount ?? 0;
              const serviceDiscountDescription = `${serviceDiscountCode.discountDescription} (${
                serviceDiscountCode.dollarDiscount === 1 ? `${serviceDiscountValue}%` : `$${serviceDiscountValue}`
              }) - ${program.title} - ${service.serviceCode}`;
              const serviceDiscountAmount = service.discountAmount ?? 0;

              service.discounts?.push({
                type: "service",
                description: serviceDiscountDescription,
                value: serviceDiscountValue,
                amount: serviceDiscountAmount,
              });
            });

          service.discounts = this.getServiceDiscounts(service, accountPaymentInfo);

          service.totalDiscount = roundPrice(_.sum(service.discounts?.map((discountInfo) => discountInfo.amount ?? 0)), "down", 2);

          // Service Total Price
          service.totalPrice = Math.max(roundPrice((service.subtotal ?? 0) - (service.totalDiscount ?? 0), "nearest", 2), 0);

          // Service Taxes
          service.taxes = this.programServiceTaxStrategies.flatMap((taxStrategy) => {
            const taxStrategyTaxes = accountPaymentInfo.taxesInfo.filter((taxInfo) => taxInfo.type === taxStrategy.getTaxInfoType());

            return taxStrategyTaxes.map((tax) => {
              return taxStrategy.getTaxInfo(tax, service);
            });
          });

          service.totalTax = roundPrice(_.sum(service.taxes.map((tax) => tax.amount ?? 0)), "nearest", 2);

          // Service Total Due
          service.totalDue = roundPrice((service.totalPrice ?? 0) + (service.totalTax ?? 0), "nearest", 2);
        });

        program.subtotal = roundPrice(_.sum(programServices.map((service) => service.subtotal ?? 0)), "nearest", 2);

        // program.monthlySubtotal

        program.discounts = this.getProgramDiscounts(program, accountPaymentInfo, programServices);

        // program.monthlyDiscounts

        program.totalDiscount = _.sum(program.discounts.map((discountInfo) => discountInfo.amount ?? 0));

        // program.totalMonthlyDiscount

        program.totalPrice = Math.max(roundPrice((program.subtotal ?? 0) - (program.totalDiscount ?? 0), "nearest", 2), 0);

        // program.totalMonthlyPrice

        program.taxes = this.getProgramTaxes(program);

        // program.monthlyTaxes

        program.totalTax = roundPrice(_.sum((program.taxes ?? []).map((programTax) => programTax.amount ?? 0)), "nearest", 2);

        // program.totalMonthlyTax

        program.totalDue = roundPrice((program.totalPrice ?? 0) + (program.totalTax ?? 0), "nearest", 2);

        // program.totalMonthlyDue

        return program;
      });

    /* calculate account */
    accountPaymentInfo.subtotal = _.sum(accountPaymentInfo.programs.map((program) => program.subtotal ?? 0));

    // accountPaymentInto.monthlySubtotal

    accountPaymentInfo.discounts = this.getAccountDiscounts(accountPaymentInfo);

    // accountPaymentInfo.monthlyDiscounts

    accountPaymentInfo.totalDiscount = roundPrice(_.sum(accountPaymentInfo.discounts.map((discount) => discount.amount ?? 0)), "nearest", 2);

    // accountPaymentInfo.totalMonthlyDiscount

    accountPaymentInfo.totalPrice = Math.max(roundPrice((accountPaymentInfo.subtotal ?? 0) - (accountPaymentInfo.totalDiscount ?? 0), "nearest", 2), 0);

    // accountPaymentInfo.totalMonthlyPrice

    accountPaymentInfo.taxes = this.getAccountTaxes(accountPaymentInfo);

    // accountPaymentInfo.monthlyTaxes

    accountPaymentInfo.totalTax = roundPrice(_.sum(accountPaymentInfo.taxes.map((tax) => tax.amount ?? 0)), "nearest", 2);

    // accountPaymentInfo.totalMonthlyTax

    accountPaymentInfo.totalDue = Math.max(roundPrice((accountPaymentInfo.totalPrice ?? 0) + (accountPaymentInfo.totalTax ?? 0) + accountPaymentInfo.accountBalance, "nearest", 2), 0);

    // accountPaymentInfo.totalMonthlyDue

    return accountPaymentInfo;
  }

  private getUnpaidProgramServices(programId: number, services: AccountProgramServiceInfo[]): AccountProgramServiceInfo[] {
    // Filter and initialize services
    return services
      .filter((service) => service.programID === programId && !service.isPaid)
      .filter((service) => !service.doneDate) // Services that are both done and due payment should already be reflected in accountBalance.
      .map((service) => ({
        ...service,
        subtotal: 0,
        discounts: [],
        totalDiscount: 0,
        totalPrice: 0,
        taxes: [],
        totalTax: 0,
        totalDue: 0,
      }));
  }

  // TODO: Implement cumulative discounting like cart.service.ts?
  private getServiceDiscounts(accountProgramServiceInfo: AccountProgramServiceInfo, accountPaymentInfo: AccountPaymentInfo): DiscountInfo[] {
    const discounts: DiscountInfo[] = [];

    accountProgramServiceInfo.discounts?.forEach((discount) => discounts.push(discount));

    this.serviceDiscountStrategies
      .forEach((programServiceDiscountStrategy) => {
        const currentDiscountedSubtotal = Math.max((accountProgramServiceInfo.subtotal ?? 0) - _.sum(discounts.map((discount) => discount.amount ?? 0)), 0);
        const discount = programServiceDiscountStrategy.getDiscountInfo(accountProgramServiceInfo, accountPaymentInfo, currentDiscountedSubtotal);
        if (discount.amount ?? 0 > 0) {
          discounts.push(discount);
        }
      });

    return discounts
      .filter((discountInfo) => (discountInfo.amount ?? 0) > 0);
  }

  // TODO: Implement cumulative discounting like cart.service.ts.
  private getProgramDiscounts(accountProgramInfo: AccountProgramInfo, accountPaymentInfo: AccountPaymentInfo, programServices: AccountProgramServiceInfo[]): DiscountInfo[] {
    const discounts: DiscountInfo[] = [];

    return discounts
      .concat(programServices.flatMap((service) => service.discounts ?? []))
      .filter((discount) => (discount.amount ?? 0) > 0);
  }

  // TODO: Implement cumulative discounting like cart.service.ts?
  private getAccountDiscounts(accountPaymentInfo: AccountPaymentInfo): DiscountInfo[] {
    const accountDiscounts: DiscountInfo[] = [];

    this.accountDiscountStrategies
      .map((accountDiscountStrategy) => accountDiscountStrategy.getDiscountInfo(accountPaymentInfo))
      .filter((discountInfo) => discountInfo.amount ?? 0 > 0);

    return this.getAggregatedDiscountInfo(accountDiscounts
      .concat(accountPaymentInfo.programs.flatMap((program) => program.discounts ?? []))
      .filter((discountInfo) => discountInfo.amount ?? 0 > 0));
  }

  private getProgramTaxes(accountProgramInfo: AccountProgramInfo): TaxInfo[] {
    const programServices = accountProgramInfo.services ?? [];
    return this.getAggregatedTaxInfo(programServices.flatMap((service) => service.taxes ?? []));
  }

  private getAccountTaxes(accountPaymentInfo: AccountPaymentInfo): TaxInfo[] {
    return this.getAggregatedTaxInfo(accountPaymentInfo.programs.flatMap((program) => program.taxes ?? []));
  }

  private getAggregatedDiscountInfo(discounts: DiscountInfo[]): DiscountInfo[] {
    const aggregatedDiscounts: DiscountInfo[] = [];

    discounts.forEach((discount) => {
      const existingDiscount = aggregatedDiscounts.find((aggregatedDiscount) => 
        aggregatedDiscount.type === discount.type &&
        aggregatedDiscount.description === discount.description &&
        aggregatedDiscount.value === discount.value
      );

      if (!!existingDiscount) {
        existingDiscount.amount = roundPrice((existingDiscount.amount ?? 0) + (discount.amount ?? 0), "nearest", 2);
      } else {
        aggregatedDiscounts.push({...discount});
      }
    });

    return aggregatedDiscounts
  }

  private getAggregatedTaxInfo(taxes: TaxInfo[]): TaxInfo[] {
    const aggregatedTaxes: TaxInfo[] = [];

    taxes.forEach((tax) => {
      const existingTax = aggregatedTaxes.find((aggregatedTax) => 
        aggregatedTax.type === tax.type &&
        aggregatedTax.description === tax.description &&
        aggregatedTax.value === tax.value
      );

      if (!!existingTax) {
        existingTax.amount = roundPrice((existingTax.amount ?? 0) + (tax.amount ?? 0), "nearest", 2);
      } else {
        aggregatedTaxes.push({...tax});
      }
    });

    return aggregatedTaxes;
  }
}
